import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';

import { finalize, take, takeUntil } from 'rxjs';

import { CardSetTransformationService, CoreService, DataService, DialogResult, DialogService, FormConfigurationService, FormNavigationService, SpinnerService } from 'dspx-core';

import {
  APP_DATA_KEYS,
  BREADCRUMBS,
  FORM_IDENTIFIERS,
  NAV_TAB_NUMBERS,
  OkButtonText,
  OPTION_SETS,
  PAGE_NUMBERS,
  POP_UP_CODES,
  POP_UP_TEXTS,
  QUESTION_CODES,
  QUESTION_PATHS,
  READ_ONLY_ACCESS_PAGE_ACTION_BUTTONS,
  SAVE_RESULT_STATUS,
  SimplePopupValidationHeader,
  UiDateFormat
} from '../../constants/app-data.constants';
import { API_ENDPOINT_KEYS, API_ENDPOINTS } from '../../constants/api-endpoints.constants';
import { TEMPLATES, VERSIONS } from '../../constants/templates.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { MaatNcqStatus } from '../../constants/maat-ncq-status.constants';
import { MaatNcqTemporaryNcqId } from '../../constants/maat-ncq.constants';
import { MaatNcqManagerDetermination } from '../../constants/maat-ncq-manager-determination.constants';
import { DEADLINE } from '../../constants/deadline.constants';

import { MaatNcqSaveDataRequest } from '../../interfaces/maat-ncq-save-data-request.interface';
import { LoggedInUserRoleFlags } from '../../interfaces/logged-in-user-role-flags.interface';
import { MaatNcqCustomSaveData } from '../../interfaces/maat-ncq-custom-save-data.interface';
import { ServerAppSettings } from '../../interfaces/server-app-settings.interface';

import { GuidParser } from '../../helpers/guid-parser';
import { FileUploadHelper } from '../../helpers/file-upload-helper';
import { DateTimeHelper } from '../../helpers/date-time-helper';
import { PagePath } from '../../helpers/config-data-parser';

import { ServiceWorkerService } from '../../services/service-worker.service';
import { HtmlHelperService } from '../../services/html-helper.service';
import { AppDataService } from '../../services/app-data.service';
import { ErrorService } from '../../services/error.service';
import { ResponseParserService } from '../../services/response-parser.service';
import { FormConfigurationParserService } from '../../services/form-configuration-parser.service';
import { MaatSorDescriptionService } from '../../services/maat-sor-description.service';
import { PublicHolidayService } from '../../services/public-holiday.service';

import { BaseCardSetDetailComponent } from '../base-card-set-detail-component/base-card-set-detail.component';

@Component({
  selector: 'app-maat-ncq',
  templateUrl: './maat-ncq.component.html',
  styleUrl: './maat-ncq.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class MaatNcqComponent extends BaseCardSetDetailComponent implements OnInit, OnDestroy {

  fieldTypeReplacements = CardSetTransformationService.cardSetWithFlatTabsFieldTypeReplacements;
  formServiceEndpoint: string;
  templateId: number = TEMPLATES.maatNcq;
  versionNo: number = VERSIONS.latest;
  activeDialog: string;
  editing = false;

  maatAuditPlanId: string = '';
  maatJobId: string = '';
  maatJobItemId: string = '';
  maatNcqStatus: string = '';
  maatNcqId: string = '';
  jobNumber: string = '';
  itemNumber: string = '';
  propertyAddress: string = '';
  loggedInUserName: string = '';
  loggedInContractorPosition: string = '';

  constructor(
    protected router: Router,
    protected serviceWorkerService: ServiceWorkerService,
    protected appDataService: AppDataService,
    protected spinnerService: SpinnerService,
    protected htmlHelperService: HtmlHelperService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected formNavigationService: FormNavigationService,
    private formConfigurationParserService: FormConfigurationParserService,
    private formConfigurationService: FormConfigurationService,
    private route: ActivatedRoute,
    private coreService: CoreService,
    private errorService: ErrorService,
    private dataService: DataService,
    private maatSorDescriptionService: MaatSorDescriptionService,
    private publicHolidayService: PublicHolidayService) {

    super(
      router,
      serviceWorkerService,
      appDataService,
      spinnerService,
      htmlHelperService,
      dialogService,
      responseParserService,
      formNavigationService);

    this.initialiseReturnNavigationState();
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((paramMap: ParamMap) => {
      this.maatAuditPlanId = paramMap.get('maatAuditPlanId');
      this.maatJobId = paramMap.get('maatJobId');
      this.maatJobItemId = paramMap.get('maatJobItemId');
      this.maatNcqId = paramMap.get('maatNcqId');
      if (this.maatNcqId) {
        this.formServiceEndpoint = API_ENDPOINT_KEYS.getMaatNcqConfigAndResponse;
        this.formConfigurationService.endpoints[this.formServiceEndpoint] = `${API_ENDPOINTS.maatNcq.getConfigAndResponse}/${this.maatJobItemId}`; // Needs to be set every time because it changes
        this.editing = true;
      }
      else {
        this.formServiceEndpoint = API_ENDPOINT_KEYS.getMaatNcqConfigWithEmptyResponse;
        this.formConfigurationService.endpoints[this.formServiceEndpoint] = `${API_ENDPOINTS.maatNcq.getConfigWithEmptyResponse}/${this.maatJobItemId}`; // Needs to be set every time because it changes
        this.resetDirtyFlagAfterLoading();
      }
    });

    this.coreService.conditionFunctions['isReadOnly'] = () => true;

    this.coreService.conditionFunctions['showContractorResponseSection'] = () => this.showContractorResponseSection();
    this.coreService.conditionFunctions['showPoSpoRecommendationSection'] = () => this.showPoSpoRecommendationSection();
    this.coreService.conditionFunctions['showSpoResponseSection'] = () => this.showSpoResponseSection();
    this.coreService.conditionFunctions['showHcAdditionalInfoSection'] = () => this.showHcAdditionalInfoSection();
    this.coreService.conditionFunctions['showSpoRecommendationSection'] = () => this.showSpoRecommendationSection();
    this.coreService.conditionFunctions['showManagerDeterminationSection'] = () => this.showManagerDeterminationSection();

    this.coreService.conditionFunctions['isRaiseNcqSectionReadonly'] = () => !this.canKeepAsDraftOrRaiseNcq();
    this.coreService.conditionFunctions['isContractorResponseSectionReadonly'] = () => !this.canSubmitResponse();
    this.coreService.conditionFunctions['isPoSpoRecommendationSectionReadOnly'] = () => !this.canReferToSpo();
    this.coreService.conditionFunctions['isSpoResponseSectionReadOnly'] = () => !this.canRequestAdditionalInformation();
    this.coreService.conditionFunctions['isHcAdditionalInfoSectionReadOnly'] = () => !this.canSubmitAdditionalInformation();
    this.coreService.conditionFunctions['isSpoRecommendationSectionReadOnly'] = () => !this.canAddSpoRecommendation();
    this.coreService.conditionFunctions['isManagerDeterminationSectionReadOnly'] = () => !this.canCompleteNcq();

    this.coreService.applicationFunctions['canSave'] = () => this.canSave();
    this.coreService.applicationFunctions['canKeepAsDraftOrRaiseNcq'] = () => this.canKeepAsDraftOrRaiseNcq();
    this.coreService.applicationFunctions['canSubmitResponse'] = () => this.canSubmitResponse();
    this.coreService.applicationFunctions['canReferToSpo'] = () => this.canReferToSpo();
    this.coreService.applicationFunctions['canReferToMgr'] = () => this.canReferToMgr();
    this.coreService.applicationFunctions['canRequestAdditionalInformation'] = () => this.canRequestAdditionalInformation();
    this.coreService.applicationFunctions['canSubmitAdditionalInformation'] = () => this.canSubmitAdditionalInformation();
    this.coreService.applicationFunctions['canCompleteNcq'] = () => this.canCompleteNcq();
    this.coreService.applicationFunctions['canDeleteNcq'] = () => this.canDeleteNcq();

    this.coreService.applicationFunctions['keepAsDraft'] = () => this.keepAsDraft();
    this.coreService.applicationFunctions['raiseNcq'] = () => this.raiseNcq();
    this.coreService.applicationFunctions['submitResponse'] = () => this.submitResponse();
    this.coreService.applicationFunctions['referToSpo'] = () => this.referToSpo();
    this.coreService.applicationFunctions['referToMgr'] = () => this.referToMgr();
    this.coreService.applicationFunctions['requestAdditionalInformation'] = () => this.requestAdditionalInformation();
    this.coreService.applicationFunctions['submitAdditionalInformation'] = () => this.submitAdditionalInformation();
    this.coreService.applicationFunctions['completeNcq'] = () => this.completeNcq();
    this.coreService.applicationFunctions['deleteNcq'] = () => this.deleteNcq();

    this.overrideHtmlTitleSetByDspxFrameworkOnFormNavigation();

    this.waitForServiceWorker()
      .then(waitResult => {
        if (waitResult?.loggedInUserProfile) {
          this.loggedInUserName = waitResult.loggedInUserProfile.fullName;
          this.loggedInContractorPosition = waitResult.loggedInUserProfile.position;
        }
      });
  }

  ngOnDestroy(): void {
    this.completeDestroy();
  }

  formLoaded(event: any): void {
    const parentId = this.maatJobItemId || '';
    const additionalData = event?.customConfigData?.[parentId] ?? {};
    const { maatAuditPlanHeading, maatJobHeading, maatJobItemHeading, maatNcqHeadingById, jobNumber, itemNumber, sorCode, propertyAddress } = additionalData;
    const id = this.maatNcqId || '';
    const maatNcqHeading = maatNcqHeadingById[id] ?? MaatNcqTemporaryNcqId;
    this.propertyAddress = propertyAddress;

    this.appDataService.setBreadcrumbs([
      { label: BREADCRUMBS.pageHeadings.auditPlans, url: ROUTE_CONSTANTS.maatAuditPlan.list },
      { label: maatAuditPlanHeading, url: `${ROUTE_CONSTANTS.maatJob.listPrefix}/${this.maatAuditPlanId}` },
      { label: maatJobHeading, url: `${ROUTE_CONSTANTS.maatJobItem.listPrefix}/${this.maatAuditPlanId}/${this.maatJobId}` },
      { label: maatJobItemHeading, url: `${ROUTE_CONSTANTS.maatNcq.listPrefix}/${this.maatAuditPlanId}/${this.maatJobId}/${this.maatJobItemId}` },
      { label: maatNcqHeading }
    ]);
    this.formConfigurationParserService.findPageConfig(this.baseForm.fields, NAV_TAB_NUMBERS.maatNcq.maatNcq, PAGE_NUMBERS.maatNcq.maatNcq).props.heading = maatNcqHeading;

    if (!this.editing) {
      this.setAutoGeneratedNcqId();
      this.setDateRaised();
      this.setAddress();
      this.setDescription(sorCode);
    }
    this.setNcqStatus();

    this.jobNumber = jobNumber;
    this.itemNumber = itemNumber;
    this.templateId = event.template;
    this.versionNo = event.versionNumber;
    this.isFormEditable = !!event?.isFormEditable;

    this.fileUploadQuestions = FileUploadHelper.getFileUploadQuestionGetModelFunctions(this.formConfigurationParserService, this.responseParserService, this.baseForm);

    this.setPreviousModel({ isAsync: true });

    const pagePaths: PagePath[] = [
      [NAV_TAB_NUMBERS.maatNcq.maatNcq, PAGE_NUMBERS.maatNcq.maatNcq]
    ];
    this.ensurePageActionButtonsAreEnabledOnFormLoad(pagePaths, READ_ONLY_ACCESS_PAGE_ACTION_BUTTONS.maatNcq);

    this.coreService.fileUploadFormIdentifiers[QUESTION_CODES.maatNcq.documentation] = FORM_IDENTIFIERS.maatNcq.documentation;
    this.coreService.fileUploadFormIdentifiers[QUESTION_CODES.maatNcq.contractorDocumentation] = FORM_IDENTIFIERS.maatNcq.contractorDocumentation;
    this.coreService.fileUploadFormIdentifiers[QUESTION_CODES.maatNcq.seniorProjectOfficerDocumentation] = FORM_IDENTIFIERS.maatNcq.seniorProjectOfficerDocumentation;
    this.coreService.fileUploadFormIdentifiers[QUESTION_CODES.maatNcq.contractorAdditionalInfoDocumentation] = FORM_IDENTIFIERS.maatNcq.contractorAdditionalInfoDocumentation;
  }

  save(event: any): void {
    const currentStatus = this.maatNcqStatus as MaatNcqStatus;

    if (currentStatus === MaatNcqStatus.informationProvided) {
      this.setQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.projectOfficerRecommendationLastUpdatedUserName, this.loggedInUserName);
      this.setQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.projectOfficerRecommendationLastUpdatedUserDate, DateTimeHelper.now().toJSDate());
    }

    if (currentStatus === MaatNcqStatus.spoInformationRequested || currentStatus === MaatNcqStatus.spoInformationOverdue) {
      this.setQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.contractorAdditionalInfoLastUpdatedUserName, this.loggedInUserName);
      this.setQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.contractorAdditionalInfoLastUpdatedUserPosition, this.loggedInContractorPosition);
      this.setQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.contractorAdditionalInfoLastUpdatedUserDate, DateTimeHelper.now().toJSDate());
    }

    if (currentStatus === MaatNcqStatus.spoInformationProvided) {
      this.setQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.seniorProjectOfficerRecommendationLastUpdatedUserName, this.loggedInUserName);
      this.setQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.seniorProjectOfficerRecommendationUpdatedUserDate, DateTimeHelper.now().toJSDate());
    }

    if (currentStatus === MaatNcqStatus.referredToMgr) {
      this.setQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.managerLastUpdatedUserName, this.loggedInUserName);
      this.setQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.managerLastUpdatedUserDate, DateTimeHelper.now().toJSDate());
    }

    const saveDataRequest: MaatNcqSaveDataRequest = {
      template: this.templateId,
      versionNumber: this.versionNo,
      id: this.maatNcqId ?? GuidParser.newGuid(), // If it is an insert, we need to generate a new GUID
      response: JSON.stringify(this.baseForm.model),
      expectedLastUpdateTime: event.lastUpdatedTime,
      isSubmit: false,
      customSaveData: {
        parentId: this.maatJobItemId,
        entityNameToParentId: {
          maatNcq: this.maatJobItemId,
          maatNcqByJob: this.maatJobId,
          maatNcqByAuditPlan: this.maatAuditPlanId
        },
        jobNumber: this.jobNumber,
        itemNumber: this.itemNumber
      }
    }

    if (!event.isFormValid) {
      this.baseForm.markAllFieldsAsTouched();
      if (!this.baseForm.fields[0]?.formControl.valid) {
        this.baseForm.showValidationSummary(true);
        this.dialogService.showPopup(POP_UP_CODES.common.validationError);
      }
      else {
        this.saveData(saveDataRequest);
      }
    }
    else {
      this.saveData(saveDataRequest);
    }
  }

  cancel(event: any): void {
    this.navigateBackToDashboard();
  }

  dialogButton_saveSuccessful_Clicked(event: any): void {
    this.navigateBackToDashboard(true);
  }

  dialogButton_deleteNcqSuccessful_Clicked(event: any): void {
    this.navigateBackToDashboard(true);
  }

  private navigateBackToDashboard(ignoreUnsavedChanges = false): void {
    if (this.returnRoute) {
      this.navigate(
        this.returnRoute,
        ignoreUnsavedChanges,
        {
          state: {
            activeTab: this.returnActiveTab
          }
        }
      );
    }
    else {
      this.navigate(
        ['/', ROUTE_CONSTANTS.maatNcq.listPrefix, this.maatAuditPlanId, this.maatJobId, this.maatJobItemId],
        ignoreUnsavedChanges,
        {
          state: {
            activeTab: NAV_TAB_NUMBERS.maatJobList.maatNcqs
          }
        }
      );
    }
  }

  private saveData(request: MaatNcqSaveDataRequest, saveSuccessfulPopupCode?: string): void {
    this.spinnerService.showSpinner();
    this.dataService.saveData(API_ENDPOINTS.maatNcq.save, request)
      .pipe(take(1),
        takeUntil(this.destroy$),
        this.errorService.errorHandler(),
        finalize(() => this.spinnerService.hideSpinner())
      )
      .subscribe(data => {
        if (data?.status === SAVE_RESULT_STATUS.common.success) {
          if (data.offline || data.message === POP_UP_CODES.common.saveSuccessful) {
            this.maatNcqId = data.id;
            this.baseForm.lastUpdatedTime = data.allocatedLastUpdatedTime;
            this.activeDialog = saveSuccessfulPopupCode ?? POP_UP_CODES.common.saveSuccessful;
            this.dialogService.showPopup(this.activeDialog);
          }
          else {
            const popupCodes: string[] = (data.message).split('|').filter(Boolean);
            const popupTextLookup: Record<string, string> = { ...POP_UP_TEXTS.maatNcq };
            const failedValidations = popupCodes.map(code => popupTextLookup[code]);
            const message =
              '<ul>' +
              failedValidations.map(m => `<li>${m}</li>`).join('') +
              '</ul>';

            this.dialogService.showSimplePopup(message, SimplePopupValidationHeader, OkButtonText);
          }
        }
      });
  }

  private setAutoGeneratedNcqId(): void {
    // Check if value already exists
    const existingNcqId = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.ncqId);

    if (existingNcqId?.value) {
      return;
    }

    this.ensureQuestionValueSet(this.baseForm.model, ...QUESTION_PATHS.maatNcq.ncqId, MaatNcqTemporaryNcqId);
  }

  private setNcqStatus(): void {
    const existingStatus = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.ncqStatus);

    if (existingStatus) {
      this.maatNcqStatus = existingStatus;
      return;
    }

    this.maatNcqStatus = MaatNcqStatus.draft;

    this.ensureQuestionValueSet(this.baseForm.model, ...QUESTION_PATHS.maatNcq.ncqStatus, MaatNcqStatus.draft);
  }

  private setDateRaised(): void {
    // Check if value already exists
    const existingDateRaised = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.dateRaised);

    if (existingDateRaised?.value) {
      return;
    }

    const formattedDate = DateTimeHelper.now().toFormat(UiDateFormat);

    this.ensureQuestionValueSet(this.baseForm.model, ...QUESTION_PATHS.maatNcq.dateRaised, formattedDate);
  }

  private setAddress(): void {
    const existingValue = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.propertyAddress);

    if (existingValue?.value) {
      return;
    }

    this.ensureQuestionValueSet(this.baseForm.model, ...QUESTION_PATHS.maatNcq.propertyAddress, this.propertyAddress);
  }

  private setDescription(sorCode: string): void {
    const maatSorDescription = this.maatSorDescriptionService.getMaatSorDescription(sorCode);
    if (maatSorDescription) {
      this.setQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.description, maatSorDescription);
    }
  }

  private showContractorResponseSection(): boolean {
    const status = this.maatNcqStatus as MaatNcqStatus;

    return [
      MaatNcqStatus.informationRequested,
      MaatNcqStatus.informationOverdue,
      MaatNcqStatus.informationProvided,
      MaatNcqStatus.referredToSpo,
      MaatNcqStatus.spoInformationRequested,
      MaatNcqStatus.spoInformationOverdue,
      MaatNcqStatus.spoInformationProvided,
      MaatNcqStatus.referredToMgr,
      MaatNcqStatus.completed
    ].includes(status);
  }

  private showPoSpoRecommendationSection(): boolean {
    const status = this.maatNcqStatus as MaatNcqStatus;

    return [
      MaatNcqStatus.informationProvided,
      MaatNcqStatus.referredToSpo,
      MaatNcqStatus.spoInformationRequested,
      MaatNcqStatus.spoInformationOverdue,
      MaatNcqStatus.spoInformationProvided,
      MaatNcqStatus.referredToMgr,
      MaatNcqStatus.completed
    ].includes(status);
  }

  private showSpoResponseSection(): boolean {
    const status = this.maatNcqStatus as MaatNcqStatus;

    return [
      MaatNcqStatus.referredToSpo,
      MaatNcqStatus.spoInformationRequested,
      MaatNcqStatus.spoInformationOverdue,
      MaatNcqStatus.spoInformationProvided,
      MaatNcqStatus.referredToMgr,
      MaatNcqStatus.completed
    ].includes(status);
  }

  private showHcAdditionalInfoSection(): boolean {
    const status = this.maatNcqStatus as MaatNcqStatus;

    return [
      MaatNcqStatus.spoInformationRequested,
      MaatNcqStatus.spoInformationOverdue,
      MaatNcqStatus.spoInformationProvided,
      MaatNcqStatus.referredToMgr,
      MaatNcqStatus.completed
    ].includes(status);
  }

  private showSpoRecommendationSection(): boolean {
    const status = this.maatNcqStatus as MaatNcqStatus;

    return [
      MaatNcqStatus.spoInformationProvided,
      MaatNcqStatus.referredToMgr,
      MaatNcqStatus.completed
    ].includes(status);
  }

  private showManagerDeterminationSection(): boolean {
    const status = this.maatNcqStatus as MaatNcqStatus;

    return [
      MaatNcqStatus.referredToMgr,
      MaatNcqStatus.completed
    ].includes(status);
  }

  private canSave(): boolean {
    const currentStatus = this.maatNcqStatus as MaatNcqStatus;

    return currentStatus && currentStatus !== MaatNcqStatus.draft && currentStatus !== MaatNcqStatus.new && currentStatus !== MaatNcqStatus.deleted;
  }

  private canKeepAsDraftOrRaiseNcq(): boolean {
    const currentStatus = this.maatNcqStatus as MaatNcqStatus;
    const roleFlags = this.appDataService.getAppData<LoggedInUserRoleFlags>(APP_DATA_KEYS.loggedInUserRoleFlags);
    const isProjectOfficer = roleFlags?.isMaatProjectOfficer;
    const isSeniorProjectOfficer = roleFlags?.isMaatSeniorProjectOfficer;

    return (isProjectOfficer || isSeniorProjectOfficer) && (!currentStatus || currentStatus === MaatNcqStatus.draft);
  }

  private canSubmitResponse(): boolean {
    const currentStatus = this.maatNcqStatus as MaatNcqStatus;
    const roleFlags = this.appDataService.getAppData<LoggedInUserRoleFlags>(APP_DATA_KEYS.loggedInUserRoleFlags);
    const isHeadContractor = roleFlags?.isMaatHeadContractor;

    return isHeadContractor && (currentStatus == MaatNcqStatus.informationRequested || currentStatus == MaatNcqStatus.informationOverdue);
  }

  private canReferToSpo(): boolean {
    const currentStatus = this.maatNcqStatus as MaatNcqStatus;
    const roleFlags = this.appDataService.getAppData<LoggedInUserRoleFlags>(APP_DATA_KEYS.loggedInUserRoleFlags);
    const isProjectOfficer = roleFlags?.isMaatProjectOfficer;
    const isSeniorProjectOfficer = roleFlags?.isMaatSeniorProjectOfficer;

    return ((isProjectOfficer || isSeniorProjectOfficer) && currentStatus == MaatNcqStatus.informationProvided);
  }

  private canReferToMgr(): boolean {
    const currentStatus = this.maatNcqStatus as MaatNcqStatus;
    const roleFlags = this.appDataService.getAppData<LoggedInUserRoleFlags>(APP_DATA_KEYS.loggedInUserRoleFlags);
    const isSeniorProjectOfficer = roleFlags?.isMaatSeniorProjectOfficer;

    return (isSeniorProjectOfficer && (currentStatus == MaatNcqStatus.referredToSpo || currentStatus == MaatNcqStatus.spoInformationProvided));
  }

  private canRequestAdditionalInformation(): boolean {
    const currentStatus = this.maatNcqStatus as MaatNcqStatus;
    const roleFlags = this.appDataService.getAppData<LoggedInUserRoleFlags>(APP_DATA_KEYS.loggedInUserRoleFlags);
    const isSeniorProjectOfficer = roleFlags?.isMaatSeniorProjectOfficer;

    return isSeniorProjectOfficer && currentStatus == MaatNcqStatus.referredToSpo;
  }

  private canSubmitAdditionalInformation(): boolean {
    const currentStatus = this.maatNcqStatus as MaatNcqStatus;
    const roleFlags = this.appDataService.getAppData<LoggedInUserRoleFlags>(APP_DATA_KEYS.loggedInUserRoleFlags);
    const isHeadContractor = roleFlags?.isMaatHeadContractor;

    return isHeadContractor && (currentStatus == MaatNcqStatus.spoInformationRequested || currentStatus == MaatNcqStatus.spoInformationOverdue);
  }

  private canAddSpoRecommendation(): boolean {
    const currentStatus = this.maatNcqStatus as MaatNcqStatus;
    const roleFlags = this.appDataService.getAppData<LoggedInUserRoleFlags>(APP_DATA_KEYS.loggedInUserRoleFlags);
    const isSeniorProjectOfficer = roleFlags?.isMaatSeniorProjectOfficer;

    return (isSeniorProjectOfficer && currentStatus == MaatNcqStatus.spoInformationProvided);
  }

  private canCompleteNcq(): boolean {
    const currentStatus = this.maatNcqStatus as MaatNcqStatus;
    const roleFlags = this.appDataService.getAppData<LoggedInUserRoleFlags>(APP_DATA_KEYS.loggedInUserRoleFlags);
    const isMaatManager = roleFlags?.isMaatManager;

    return isMaatManager && currentStatus == MaatNcqStatus.referredToMgr;
  }

  private canDeleteNcq(): boolean {
    if (!this.editing) {
      return false;
    }

    const currentStatus = this.maatNcqStatus as MaatNcqStatus;
    const roleFlags = this.appDataService.getAppData<LoggedInUserRoleFlags>(APP_DATA_KEYS.loggedInUserRoleFlags);
    const isProjectOfficer = roleFlags?.isMaatProjectOfficer;
    const isSeniorProjectOfficer = roleFlags?.isMaatSeniorProjectOfficer;

    return (isProjectOfficer || isSeniorProjectOfficer) && (currentStatus == MaatNcqStatus.draft || currentStatus == MaatNcqStatus.new);
  }

  private executeActionSave(customFlags: Partial<MaatNcqCustomSaveData>): void {
    if (!customFlags?.isDeleteNcq) {
      if (!this.baseForm.fields[0]?.formControl.valid) {
        this.baseForm.markAllFieldsAsTouched();
        this.baseForm.showValidationSummary(true);
        this.dialogService.showPopup(POP_UP_CODES.common.validationError);
        return;
      }
    }
    const customSaveData: MaatNcqCustomSaveData = {
      parentId: this.maatJobItemId,
      entityNameToParentId: {
        maatNcq: this.maatJobItemId,
        maatNcqByJob: this.maatJobId,
        maatNcqByAuditPlan: this.maatAuditPlanId
      },
      jobNumber: this.jobNumber,
      itemNumber: this.itemNumber,
      ...customFlags
    };

    if (customFlags?.isRequestAdditionalInformation) {
      const serverAppSettings = this.appDataService.getAppData<ServerAppSettings>(APP_DATA_KEYS.serverAppSettings);
      const reIssueResponseRequiredBy = this.publicHolidayService.addWorkingDaysFromHours(
        DateTimeHelper.now(),
        serverAppSettings.maatNcqInitialNotificationExpiryHours,
        DEADLINE.MaatNcq
      );

      this.setQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.reIssueResponseRequiredBy, reIssueResponseRequiredBy.toFormat(UiDateFormat));
    }

    if (!this.validateNcqForm(customSaveData)) {
      return;
    }

    const documentationConfig = [
      {
        condition: customSaveData.isRaiseNcq,
        popup: POP_UP_CODES.maatNcq.confirmDocumentation,
        documentation: this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.documentation)
      },
      {
        condition: customSaveData.isSubmitResponse,
        popup: POP_UP_CODES.maatNcq.confirmContractorDocumentation,
        documentation: this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.contractorDocumentation)
      },
      {
        condition: customSaveData.isRequestAdditionalInformation,
        popup: POP_UP_CODES.maatNcq.confirmSeniorProjectOfficerDocumentation,
        documentation: this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.seniorProjectOfficerDocumentation)
      },
      {
        condition: customSaveData.isSubmitAdditionalInformation,
        popup: POP_UP_CODES.maatNcq.confirmContractorDocumentation,
        documentation: this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.contractorAdditionalInfoDocumentation)
      }
    ].find(x => x.condition);

    if (documentationConfig) {
      if (!documentationConfig.documentation) {
        this.dialogService.showConfirmPopup(documentationConfig.popup)
          .pipe(take(1))
          .subscribe(data => {
            if (data === DialogResult.ACCEPTED) {
              this.continueSave(customSaveData);
            }
          });

        return;
      }
    }

    this.continueSave(customSaveData);
  }

  private continueSave(customSaveData: MaatNcqCustomSaveData): void {
    const saveDataRequest: MaatNcqSaveDataRequest = {
      template: this.templateId,
      versionNumber: this.versionNo,
      id: this.maatNcqId ?? GuidParser.newGuid(),
      response: JSON.stringify(this.baseForm.model),
      expectedLastUpdateTime: this.baseForm.lastUpdatedTime,
      isSubmit: false,
      customSaveData
    };

    if (customSaveData.isDeleteNcq) {
      this.dialogService.showConfirmPopup(POP_UP_CODES.maatNcq.deleteNcqConfirmation)
        .pipe(take(1))
        .subscribe(data => {
          if (data === DialogResult.ACCEPTED) {
            this.saveData(saveDataRequest, POP_UP_CODES.maatNcq.deleteNcqSuccessful);
          }
        });
    }
    else {
      this.saveData(saveDataRequest);
    }
  }

  private keepAsDraft(): void {
    this.executeActionSave({});
  }

  private raiseNcq(): void {
    this.executeActionSave({
      isRaiseNcq: true
    });
  }

  private submitResponse(): void {
    this.executeActionSave({
      isSubmitResponse: true
    });
  }

  private referToSpo(): void {
    this.setQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.projectOfficerRecommendationLastUpdatedUserName, this.loggedInUserName);
    this.setQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.projectOfficerRecommendationLastUpdatedUserDate, DateTimeHelper.now().toJSDate());

    this.executeActionSave({
      isReferToSpo: true
    });
  }

  private requestAdditionalInformation(): void {
    this.executeActionSave({
      isRequestAdditionalInformation: true
    });
  }

  private submitAdditionalInformation(): void {
    this.setQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.contractorAdditionalInfoLastUpdatedUserName, this.loggedInUserName);
    this.setQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.contractorAdditionalInfoLastUpdatedUserPosition, this.loggedInContractorPosition);
    this.setQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.contractorAdditionalInfoLastUpdatedUserDate, DateTimeHelper.now().toJSDate());

    this.executeActionSave({
      isSubmitAdditionalInformation: true
    });
  }

  private referToMgr(): void {
    const currentStatus = this.maatNcqStatus as MaatNcqStatus;

    if (currentStatus == MaatNcqStatus.spoInformationProvided || currentStatus == MaatNcqStatus.referredToSpo) {
      this.setQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.seniorProjectOfficerRecommendationLastUpdatedUserName, this.loggedInUserName);
      this.setQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.seniorProjectOfficerRecommendationUpdatedUserDate, DateTimeHelper.now().toJSDate());
    }

    this.executeActionSave({
      isReferToManager: currentStatus == MaatNcqStatus.spoInformationProvided,
      isReferredNcqReferToManager: currentStatus != MaatNcqStatus.spoInformationProvided
    });
  }

  private completeNcq(): void {
    this.setQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.managerLastUpdatedUserName, this.loggedInUserName);
    this.setQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.managerLastUpdatedUserDate, DateTimeHelper.now().toJSDate());

    this.executeActionSave({
      isCompleteNcq: true
    });
  }

  private deleteNcq(): void {
    this.executeActionSave({
      isDeleteNcq: true
    });
  }

  private validateNcqForm(customSaveData: MaatNcqCustomSaveData): boolean {
    let validations: { invalid: boolean, popupText: string }[] = [];

    if (customSaveData.isRaiseNcq) {
      const category = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.category);
      const description = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.description);
      const actionProposed = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.actionProposed);
      const tradeCode = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.tradeCode);

      let isValidNonConformanceAmount = true;
      let isValidTotalItemCostPercentage = true;

      if (category === OPTION_SETS.maatNcq.Category.Overcharge || category === OPTION_SETS.maatNcq.Category.DefectiveWorks) {
        const nonformanceAmount = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.nonConformanceAmount);

        if (!nonformanceAmount) {
          isValidNonConformanceAmount = false;
        }
      }

      if (category === OPTION_SETS.maatNcq.Category.DefectiveWorks) {
        const totalItemCostPercentage = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.totalItemCostPercentage);

        if (!totalItemCostPercentage) {
          isValidTotalItemCostPercentage = false;
        }
      }

      validations = [
        { invalid: !category, popupText: POP_UP_TEXTS.maatNcq.categoryIsMandatory },
        { invalid: !isValidNonConformanceAmount, popupText: POP_UP_TEXTS.maatNcq.nonConformanceAmountIsMandatory },
        { invalid: !isValidTotalItemCostPercentage, popupText: POP_UP_TEXTS.maatNcq.totalItemCostPercentageIsMandatory },
        { invalid: !description, popupText: POP_UP_TEXTS.maatNcq.descriptionIsMandatory },
        { invalid: !actionProposed, popupText: POP_UP_TEXTS.maatNcq.actionProposedIsMandatory },
        { invalid: !tradeCode, popupText: POP_UP_TEXTS.maatNcq.tradeCodeIsMandatory }
      ];
    }

    if (customSaveData.isSubmitResponse) {
      const category = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.category)?.toLowerCase();

      if (this.canSubmitResponse()) {
        const contractorPosition = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.contractorPosition);
        const causesConsequences = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.causesConsequences);
        const proposedAction = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.proposedAction);
        const preventativeMeasures = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.preventativeMeasures);

        let isValidNonConformanceAmount = true;
        let isValidLegalCompliance = true;

        if (category === OPTION_SETS.maatNcq.Category.Overcharge && contractorPosition === OPTION_SETS.maatNcq.ContractorPosition.Agreed) {
          const hcNonConformanceAmount = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.hcNonConformanceAmount);
          const isContractorLegalAccepted = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.isContractorLegalAccepted);

          if (!hcNonConformanceAmount) {
            isValidNonConformanceAmount = false;
          }

          if (!isContractorLegalAccepted) {
            isValidLegalCompliance = false;
          }
        }

        validations = [
          { invalid: !contractorPosition, popupText: POP_UP_TEXTS.maatNcq.contractorPositionIsMandatory },
          { invalid: !causesConsequences, popupText: POP_UP_TEXTS.maatNcq.causesConsequencesIsMandatory },
          { invalid: !proposedAction, popupText: POP_UP_TEXTS.maatNcq.proposedActionIsMandatory },
          { invalid: !preventativeMeasures, popupText: POP_UP_TEXTS.maatNcq.preventativeMeasuresIsMandatory },
          { invalid: !isValidNonConformanceAmount, popupText: POP_UP_TEXTS.maatNcq.hcNonConformanceAmountIsMandatory },
          { invalid: !isValidLegalCompliance, popupText: POP_UP_TEXTS.maatNcq.contractorLegalAcceptanceIsMandatory }
        ];
      }
    }

    if (customSaveData.isReferToSpo) {
      if (this.canReferToSpo()) {
        const businessPositionProjectOfficerRecommendation = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.businessPositionProjectOfficerRecommendation);
        const projectOfficerRecommendation = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.projectOfficerRecommendation);
        const reInspectFee = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.reInspectFee);

        validations = [
          { invalid: !projectOfficerRecommendation, popupText: POP_UP_TEXTS.maatNcq.projectOfficerRecommendationIsMandatory },
          { invalid: !businessPositionProjectOfficerRecommendation, popupText: POP_UP_TEXTS.maatNcq.businessPositionProjectOfficerRecommendationIsMandatory },
          { invalid: !reInspectFee, popupText: POP_UP_TEXTS.maatNcq.reInspectFeeIsMandatory }
        ];
      }
    }

    if (customSaveData.isRequestAdditionalInformation || customSaveData.isReferredNcqReferToManager) {
      if (this.canRequestAdditionalInformation()) {
        const seniorProjectOfficerQueryAndComments = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.seniorProjectOfficerQueryAndComments);
        const action = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.action);

        validations = [
          { invalid: !seniorProjectOfficerQueryAndComments, popupText: POP_UP_TEXTS.maatNcq.seniorProjectOfficerQueryAndCommentsIsMandatory },
          { invalid: !action, popupText: POP_UP_TEXTS.maatNcq.actionIsMandatory }
        ];
      }
    }

    if (customSaveData.isSubmitAdditionalInformation) {
      if (this.canSubmitAdditionalInformation()) {
        const contractorAdditionalInfoResponse = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.contractorAdditionalInfoResponse);

        validations = [
          { invalid: !contractorAdditionalInfoResponse, popupText: POP_UP_TEXTS.maatNcq.contractorAdditionalInfoResponseIsMandatory }
        ];
      }
    }

    if (customSaveData.isReferToManager) {
      if (this.canReferToMgr()) {
        const seniorProjectOfficerRecommendation = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.seniorProjectOfficerRecommendation);
        const businessPosition = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.businessPosition);

        validations = [
          { invalid: !seniorProjectOfficerRecommendation, popupText: POP_UP_TEXTS.maatNcq.seniorProjectOfficerRecommendationIsMandatory },
          { invalid: !businessPosition, popupText: POP_UP_TEXTS.maatNcq.businessPositionIsMandatory }
        ];
      }
    }

    if (customSaveData.isCompleteNcq) {
      if (this.canCompleteNcq()) {
        const managerTradeCode = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.managerTradeCode);
        const managerCategory = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.managerCategory);
        const managerDetermination = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.managerDetermination);
        const managerNonConformanceAmount = this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatNcq.managerNonConformanceAmount);
        const isOverChargeOrDefectiveworks = managerCategory === OPTION_SETS.maatNcq.Category.Overcharge || managerCategory === OPTION_SETS.maatNcq.Category.DefectiveWorks;
        const isSustained = MaatNcqManagerDetermination.SustainedDeterminations.has(managerDetermination);

        let isValidNonConformanceAmount = true;

        if (isOverChargeOrDefectiveworks && isSustained && !managerNonConformanceAmount) {
          isValidNonConformanceAmount = false;
        }

        validations = [
          { invalid: !managerTradeCode, popupText: POP_UP_TEXTS.maatNcq.managerTradeCodeIsMandatory },
          { invalid: !managerCategory, popupText: POP_UP_TEXTS.maatNcq.managerCategoryIsMandatory },
          { invalid: !managerDetermination, popupText: POP_UP_TEXTS.maatNcq.managerDeterminationIsMandatory },
          { invalid: !isValidNonConformanceAmount, popupText: POP_UP_TEXTS.maatNcq.managerNonConformanceAmountIsMandatory }
        ];
      }
    }

    const failedValidations = validations.filter(v => v.invalid);

    if (failedValidations.length > 0) {
      this.baseForm.markAllFieldsAsTouched();

      const message =
        `<ul>` +
        failedValidations.map(v => `<li>${v.popupText}</li>`).join('') +
        `</ul>`;

      this.dialogService.showSimplePopup(message, SimplePopupValidationHeader, OkButtonText);

      return false;
    }
    return true;
  }
}
